import { Button } from "@benefit-systems/common-components";
import ActiveFormData from "@feniks-pwa/web-app/web/Pages/Checkout/Subscription/BenefitSummary/ActiveFormData/activeFormData";
import { FieldArray, useFormikContext } from "formik";
import { arrayOf, bool, func, object, shape, string } from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import ActiveForm from "web/Pages/Checkout/Subscription/BenefitSummary/ActiveForm";
import CompletedForm from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/CompletedForm";
import DuplicateAlreadyOrdered from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/DuplicateAlreadyOrdered/duplicateAlreadyOrdered";
import NotificationText from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/NotificationText/notificationText";
import {
  FORM_RECEIVERS,
  FORM_TYPES,
  FORM_TYPES_BY_RECEIVERS,
  newDataIsNotSavedText,
  newDataIsSavedText,
  newEmailIsSavedText,
} from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/constants";
import {
  getData,
  onActiveFormSubmitSuccess,
  updateDataInDraft,
} from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/utils";
import Modal from "web/Pages/Checkout/Subscription/Modal";

import Loading from "web/Layout/Common/Loading/loading";
import __ from "web/Layout/Translations";

import HtmlTemplate from "web/Components/Common/HtmlTemplate";
import TransparentButton from "web/Components/Common/TransparentButton/transparentButton";

import EditIcon from "web/assets/icons/edit.svg";

import useModalNotification from "web/hooks/useModalNotification";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import checkIfTimePassed from "web/utils/data/validator/dateAndTime/checkIfTimePassed";
import addDuplicateOrderStatus from "web/utils/page/product/subscription/addDuplicateOrderStatus";
import getValuesFromFieldsStructured from "web/utils/page/product/subscription/getValuesFromFieldsStructured";

import { BENEFIT_STATUS, DuplicateStatus } from "web/constants/benefits";
import { dayMilliseconds } from "web/constants/date";

import { FormPreviewMode, FormView } from "web/types/Subscription";

import classify from "web/classify";
import { useAppContext } from "web/context/app";
import { setNotificationError } from "web/features/app/appSlice";
import { useGetEmployeeConfigurationQuery } from "web/features/employee/employeeApiSlice";

import DuplicateCardForm from "../DuplicateCardForm/duplicateCardForm";
import defaultClasses from "./benefitForm.scss";
import getFormStatus from "./getFormStatus";

const DEFAULT_FORMS_TO_UPDATE = {
  benefitId: null,
  wayChooseOthersInActiveForm: null,
  [FORM_TYPES.ACTIVE_FORMS]: {
    [FORM_TYPES_BY_RECEIVERS.EMPLOYEE_FORMS]: [],
    [FORM_TYPES_BY_RECEIVERS.OTHERS_FORMS]: [],
  },
  [FORM_TYPES.FORMS]: {
    [FORM_TYPES_BY_RECEIVERS.EMPLOYEE_FORMS]: [],
    [FORM_TYPES_BY_RECEIVERS.OTHERS_FORMS]: [],
  },
};

const CheckoutSubscriptionBenefitForm = ({
  classes,
  forms,
  activeForms,
  setModal,
  benefitId,
  wayChooseOthersInActiveForm,
  benefitUUID,
  isMyBenefitsView,
  handleItemUpdate,
  handleToggleEditModal,
  formsEditedByUsers,
  setFormsEditedByUsers,
  duplicateReceiverData,
  duplicateCardDataObject,
  setDuplicateReceiverData,
  onFormUpdateSucceeded,
  formsToUpdate,
  enableOrderDuplicateCard,
  benefitStatus,
  outerFormValues,
  benefitGroupTypeItem,
}) => {
  const { data: employeeConfig } = useGetEmployeeConfigurationQuery();
  const {
    subscriptionAllowChangingPersonalData,
    subscriptionAllowChangingPersonalDataUMS,
    formEditInfoBlockEmployee,
    formEditInfoBlockEmployeeUMS,
    formEditInfoBlockOthers,
    formEditInfoBlockOthersUMS,
  } = employeeConfig || {};
  const { benefitGroupType } = useSelector((state) => state.subscriptionQuote);
  const prepBenefitGroupType = benefitGroupTypeItem
    ? benefitGroupTypeItem.toString()
    : benefitGroupType;
  const [formDuplicatInfoStepTwo, setFormDuplicatInfoStepTwo] = useState([]);

  const currentForms = isArrayHasItems(forms) ? forms : activeForms;

  const { forms: outerForms, activeForms: outerActiveForms } = outerFormValues;

  if (
    isArrayHasItems(currentForms) &&
    !isArrayHasItems(formDuplicatInfoStepTwo)
  )
    setFormDuplicatInfoStepTwo(
      addDuplicateOrderStatus(currentForms, enableOrderDuplicateCard)
    );

  const { setFieldValue, values } = useFormikContext();
  const { modal } = useAppContext();
  const { dispatch } = modal;
  const closeActiveFormModal = useCallback(() => {
    dispatch({ type: "reset" });
  }, [dispatch]);

  const { duplicateCardData, showDuplicateForm } = duplicateCardDataObject;

  const {
    [FORM_TYPES.ACTIVE_FORMS]: {
      [FORM_TYPES_BY_RECEIVERS.EMPLOYEE_FORMS]: employeeActiveFormsToUpdate,
      [FORM_TYPES_BY_RECEIVERS.OTHERS_FORMS]: othersActiveFormsToUpdate,
    },
    [FORM_TYPES.FORMS]: {
      [FORM_TYPES_BY_RECEIVERS.EMPLOYEE_FORMS]: employeeFormsToUpdate,
      [FORM_TYPES_BY_RECEIVERS.OTHERS_FORMS]: othersFormsToUpdate,
    },
  } = formsToUpdate;

  const allFormsToUpdate = [
    ...employeeActiveFormsToUpdate,
    ...othersActiveFormsToUpdate,
    ...employeeFormsToUpdate,
    ...othersFormsToUpdate,
  ];

  const willFormBeSent = wayChooseOthersInActiveForm === FORM_RECEIVERS.OTHERS;

  const allowChangingPersonalData =
    prepBenefitGroupType === "2"
      ? subscriptionAllowChangingPersonalDataUMS === "1"
      : subscriptionAllowChangingPersonalData === "1";

  const [editingFormId, setEditingFormId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const infoBlockEmployee =
    prepBenefitGroupType === "2"
      ? formEditInfoBlockEmployeeUMS
      : formEditInfoBlockEmployee;
  const infoBlockOthers =
    prepBenefitGroupType === "2"
      ? formEditInfoBlockOthersUMS
      : formEditInfoBlockOthers;

  const [showModalNotification, setModalLoaderNotification] =
    useModalNotification();

  useEffect(() => {
    activeForms.forEach((activeForm) => {
      if (activeForm.completed) {
        handleActiveFormSubmit(
          getValuesFromFieldsStructured(activeForm.fieldsStructured),
          activeForm.id,
          true
        );
      }
    });
  }, [activeForms]);

  const handleActiveFormSubmit = (
    activeFormValues,
    formId,
    isAutomaticallySet,
    isEditMode,
    formUUID,
    receiver
  ) => {
    const field = `${benefitId}.activeForms.${formId}.fields`;
    const newValue = activeFormValues;

    if (!isEditMode) {
      setFieldValue(field, activeFormValues, false);
    }

    if (isEditMode) {
      setModalLoaderNotification();
      setIsLoading(true);

      const newBenefitData = {
        id: formUUID,
        receiver,
        fields: newValue,
      };

      updateDataInDraft(
        benefitUUID,
        newBenefitData,
        FORM_TYPES.ACTIVE_FORMS,
        () => {
          if (typeof onFormUpdateSucceeded === "function") {
            onFormUpdateSucceeded(
              benefitId,
              formId,
              FORM_TYPES.ACTIVE_FORMS,
              receiver
            );
          }
          return onActiveFormSubmitSuccess(
            handleItemUpdate,
            formId,
            newValue,
            setFormsEditedByUsers,
            formUUID,
            setFieldValue,
            field,
            activeFormValues,
            showModalNotification,
            handleToggleEditModal,
            setIsLoading,
            isMyBenefitsView
          );
        },
        () => {
          dispatch(
            setNotificationError({ message: __(newDataIsNotSavedText) })
          );
          setIsLoading(false);
        },
        isMyBenefitsView
      );
    }

    if (!isAutomaticallySet && !isEditMode) closeActiveFormModal();
  };

  const handleShowModal = (
    { id, name, description, data, receiver, itemActive, fieldsStructured },
    editFormEvenIfCompleted,
    isCompleted
  ) => {
    const processedOuterFormCtx = outerActiveForms?.[id]?.fields;
    const formInputValue =
      processedOuterFormCtx || values[benefitId].activeForms[id].fields;

    const activeFormValues =
      formInputValue || getValuesFromFieldsStructured(fieldsStructured);
    // modal only for ACTIVE forms with receiver type employee (only interested in data attribute)
    setModal(
      <Modal
        title={name || __("Formularz świadczeń abonamentowych")}
        description={description}
      >
        {isCompleted && !editFormEvenIfCompleted ? (
          <ActiveFormData data={data} values={activeFormValues} />
        ) : (
          <ActiveForm
            data={data}
            receiver={receiver}
            formValues={activeFormValues}
            formId={id}
            onSubmit={handleActiveFormSubmit}
            isEditMode={!!itemActive}
            formUUID={id}
            close={closeActiveFormModal}
            allowChangingPersonalData={allowChangingPersonalData}
            outerFormValues={processedOuterFormCtx}
          />
        )}
      </Modal>
    );
  };

  const getFormsFromFields = ({
    formList,
    indexOffset = 0,
    description = "",
    descriptionInfo = "",
    formType = FORM_TYPES.FORMS,
    isMyBenefitsView,
  }) => {
    const formFields = formList.map((singleForm, index) => {
      const {
        fieldsStructured,
        fieldsConfiguration,
        id,
        receiver,
        receiverEmail,
        receiverId,
        emailSentAt,
        awaitsUpdate,
        ignoreValidate,
      } = Object(singleForm);

      const formStatus = getFormStatus({
        form: singleForm,
        templateView: isMyBenefitsView
          ? FormView.MY_BENEFITS
          : FormView.CONFIGURATOR,
        allowChangingPersonalData,
        wayChooseOthersInActiveForm,
      });

      const doesEmailNeedToBeResend =
        formType === FORM_TYPES.ACTIVE_FORMS &&
        receiver === FORM_RECEIVERS.OTHERS &&
        awaitsUpdate &&
        (!emailSentAt ||
          (emailSentAt && checkIfTimePassed(emailSentAt, dayMilliseconds)));

      return singleForm && fieldsStructured
        ? getSingleForm({
            fieldsStructured,
            id,
            receiver,
            index: index + indexOffset,
            formType,
            formStatus,
            receiverEmail,
            uuid: id,
            receiverId,
            doesEmailNeedToBeResend,
            fieldsConfiguration,
            ignoreValidate,
          })
        : null;
    });

    return (
      <>
        {!isMyBenefitsView && (
          <div className={classes.descriptionContainer}>
            <strong className={classes.description}>{description}</strong>
            {descriptionInfo ? (
              <p className={classes.descriptionInfo}>{descriptionInfo}</p>
            ) : null}
          </div>
        )}
        <div className={classes.fieldListGrid}>{formFields}</div>
      </>
    );
  };

  const getSingleForm = ({
    fieldsStructured,
    fieldsConfiguration,
    id,
    receiver,
    index,
    formType,
    formStatus,
    receiverEmail,
    uuid,
    onEditBtnClick,
    receiverId,
    doesEmailNeedToBeResend,
    doesNeedUpdate,
    ignoreValidate,
  }) => {
    const doesFormNeedUpdate =
      typeof doesNeedUpdate === "boolean"
        ? doesNeedUpdate
        : allFormsToUpdate.some((fId) => fId === id);

    const getLegend = (floated) => (
      <legend className={floated ? classes.legendFloated : classes.legend}>{`${
        index + 1
      }. ${__("osoba")}:`}</legend>
    );

    const benefitFormsWithDuplicateStatus = formDuplicatInfoStepTwo.find(
      (ele) => ele.id === id
    );

    const isActiveForm = formType === FORM_TYPES.ACTIVE_FORMS;

    const isUnderliningNeeded =
      isActiveForm && willFormBeSent
        ? doesEmailNeedToBeResend && doesFormNeedUpdate
        : doesFormNeedUpdate;

    const outerFormCtxValue = isActiveForm ? outerActiveForms : outerForms;

    const processedOuterForm = outerFormCtxValue?.[id]?.fields;

    return fieldsStructured ? (
      <div className={classes.smallForm} key={uuid}>
        <fieldset
          className={classes.fieldset}
          key={id}
          data-t1={`${index + 1}. ${__("osoba")}:`}
        >
          {!formStatus.isCompleted && (
            <>
              {getLegend(true)}
              <div className={classes.fieldList}>
                {getData(
                  fieldsStructured,
                  receiver,
                  id,
                  formType,
                  benefitId,
                  false,
                  allowChangingPersonalData
                )}
              </div>
            </>
          )}
        </fieldset>
        {formStatus.isCompleted && (
          <CompletedForm
            formId={id}
            benefitId={benefitId}
            fieldsStructured={fieldsStructured}
            fieldsConfiguration={fieldsConfiguration}
            getLegend={getLegend}
            receiverEmail={receiverEmail}
            formUUID={uuid}
            benefitUUID={benefitUUID}
            receiver={receiver}
            isMyBenefitsView={isMyBenefitsView}
            handleItemUpdate={handleItemUpdate}
            setEditingFormId={setEditingFormId}
            isEditing={editingFormId === id}
            customOnEditBtnClick={onEditBtnClick}
            handleToggleEditModal={handleToggleEditModal}
            setFormsEditedByUsers={setFormsEditedByUsers}
            formStatus={formStatus}
            setDuplicateReceiverData={setDuplicateReceiverData}
            index={index}
            duplicateCardData={duplicateCardData}
            duplicateReceiverData={duplicateReceiverData}
            receiverId={receiverId}
            showDuplicateForm={showDuplicateForm}
            benefitFormsWithDuplicateStatus={benefitFormsWithDuplicateStatus}
            doesEmailNeedToBeResend={doesEmailNeedToBeResend}
            onFormUpdateSucceeded={onFormUpdateSucceeded}
            isUnderliningNeeded={isUnderliningNeeded}
            willFormBeSent={willFormBeSent}
            setFormDuplicatInfo={setFormDuplicatInfoStepTwo}
            benefitStatus={benefitStatus}
            allowChangingPersonalData={allowChangingPersonalData}
            isActiveForm={isActiveForm}
            outerFormValues={processedOuterForm}
            ignoreValidate={ignoreValidate}
          />
        )}
        {formsEditedByUsers.includes(uuid) && (
          <div className={classes.notificationContainer}>
            <NotificationText
              header={__(
                receiverEmail ? newEmailIsSavedText : newDataIsSavedText
              )}
              descriptionComponent={
                <HtmlTemplate
                  className="cke_editable"
                  template={__(
                    receiver === FORM_RECEIVERS.EMPLOYEE
                      ? infoBlockEmployee
                      : infoBlockOthers
                  )}
                />
              }
            />
          </div>
        )}
      </div>
    ) : null;
  };

  const getForms = (forms, formDescription, formAdditionalDescription) => {
    return (
      <>
        <strong className={classes.description}>{__(formDescription)}</strong>
        {formAdditionalDescription && (
          <strong className={classes.additionalDescription}>
            {__(formAdditionalDescription)}
          </strong>
        )}
        <div className={classes.fieldListGrid}>
          {forms.map((singleForm, index) => {
            const {
              data,
              fieldsStructured,
              fieldsConfiguration,
              id,
              receiver,
              receiverId,
            } = Object(singleForm);

            const doesFormNeedUpdate = allFormsToUpdate.some(
              (fId) => fId === id
            );

            const benefitFormsWithDuplicateStatus =
              formDuplicatInfoStepTwo.find((ele) => ele.id === id);

            const formStatus = getFormStatus({
              form: singleForm,
              templateView: isMyBenefitsView
                ? FormView.MY_BENEFITS
                : FormView.CONFIGURATOR,
              allowChangingPersonalData,
              wayChooseOthersInActiveForm,
              doesFormNeedUpdate,
            });

            const { isCompleted, previewMode, isEditable } = formStatus;

            if (
              isCompleted &&
              previewMode === FormPreviewMode.EDITABLE_PREVIEW
            ) {
              return getSingleForm({
                fieldsStructured,
                fieldsConfiguration,
                id,
                receiver,
                index,
                formType: FORM_TYPES.ACTIVE_FORMS,
                formStatus,
                receiverEmail: null,
                uuid: id,
                onEditBtnClick: () => handleShowModal(singleForm, true),
                receiverId,
                doesFormNeedUpdate,
              });
            }

            const currentActiveDuplicateID = `${index}-${id}`;
            const duplicateView = duplicateCardData &&
              benefitFormsWithDuplicateStatus && (
                <span>
                  {benefitFormsWithDuplicateStatus.duplicateOrderStatus ===
                    DuplicateStatus.NEVER_ORDERED &&
                    benefitStatus === BENEFIT_STATUS.STATUS_ACTIVE &&
                    (duplicateReceiverData.index !== currentActiveDuplicateID ||
                      duplicateReceiverData.receiver !== receiver) && (
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() =>
                          setDuplicateReceiverData({
                            showForm: true,
                            index: currentActiveDuplicateID,
                            receiver,
                          })
                        }
                        disabled={duplicateReceiverData.showForm}
                      >
                        {__("Zamów duplikat")}
                      </Button>
                    )}
                  {duplicateReceiverData.showForm &&
                    duplicateReceiverData.index === currentActiveDuplicateID &&
                    duplicateReceiverData.receiver === receiver && (
                      <DuplicateCardForm
                        duplicateCardData={duplicateCardData}
                        receivers={receiverId}
                        showDuplicateForm={showDuplicateForm}
                        setDuplicateReceiverData={setDuplicateReceiverData}
                        setFormDuplicatInfo={setFormDuplicatInfoStepTwo}
                        benefitFormsWithDuplicateStatus={
                          benefitFormsWithDuplicateStatus
                        }
                      />
                    )}
                  {benefitFormsWithDuplicateStatus.duplicateOrderStatus ===
                    DuplicateStatus.ALREADY_ORDERED && (
                    <DuplicateAlreadyOrdered
                      duplicateCardData={duplicateCardData}
                    />
                  )}
                </span>
              );

            const BtnComponent = isCompleted ? TransparentButton : "button";
            return singleForm && data ? (
              <div key={id}>
                <fieldset
                  className={classes.fieldset}
                  data-t1={`${index + 1}. ${__("osoba")}:`}
                >
                  <legend className={classes.legendFloated}>{`${
                    index + 1
                  }. ${__("osoba")}:`}</legend>
                  <>
                    <span className={classes.buttonContainer}>
                      <BtnComponent
                        type="button"
                        onClick={() =>
                          handleShowModal(
                            singleForm,
                            doesFormNeedUpdate,
                            isCompleted
                          )
                        }
                        key={`${singleForm.id}_button`}
                        className={`${
                          doesFormNeedUpdate ? classes.errorBorder : ""
                        } ${isCompleted ? "" : classes.button}`.trim()}
                      >
                        {isCompleted
                          ? __("Zobacz formularz")
                          : __("Wypełnij formularz")}
                      </BtnComponent>
                    </span>
                    {isEditable ? (
                      <span className={classes.buttonContainer}>
                        <TransparentButton
                          type="button"
                          onClick={() => handleShowModal(singleForm, true)}
                          key={`${singleForm.id}_button`}
                        >
                          <EditIcon className={classes.btnIcon} />
                          <span>{__("Edytuj")}</span>
                        </TransparentButton>
                      </span>
                    ) : null}
                    {duplicateView}
                  </>
                </fieldset>
                {formsEditedByUsers.includes(id) && (
                  <NotificationText
                    header={__(newDataIsSavedText)}
                    descriptionComponent={
                      <HtmlTemplate
                        className="cke_editable"
                        template={__(
                          receiver === FORM_RECEIVERS.EMPLOYEE
                            ? infoBlockEmployee
                            : infoBlockOthers
                        )}
                      />
                    }
                  />
                )}
              </div>
            ) : null;
          })}
        </div>
      </>
    );
  };

  const activeFields = useMemo(() => {
    // ACTIVE form option

    if (isArrayHasItems(activeForms)) {
      // different form types are split into groups by their receiver type.
      const [employeeForms, otherForms] = activeForms.reduce(
        ([employee, other], singleForm) => {
          return singleForm?.receiver === FORM_RECEIVERS.EMPLOYEE
            ? [[...employee, singleForm], other]
            : [employee, [...other, singleForm]];
        },
        [[], []]
      );
      return (
        <>
          {!!employeeForms.length &&
            getForms(
              employeeForms,
              "Dane osób dla których wybierasz abonament.",
              ""
            )}
          {!!otherForms?.length &&
            (wayChooseOthersInActiveForm === FORM_RECEIVERS.OTHERS
              ? getFormsFromFields({
                  formList: otherForms,
                  indexOffset: employeeForms.length ? 1 : 0,
                  description: __(
                    "Dane osób, dla których wybierasz abonament."
                  ),
                  descriptionInfo: __(
                    "Na podany e-mail zostanie wysłany formularz."
                  ),
                  formType: FORM_TYPES.ACTIVE_FORMS,
                  isMyBenefitsView,
                })
              : getForms(
                  otherForms,
                  "Dane osób dla których wybierasz abonament.",
                  "Uzupełnij dane osób dodatkowych."
                ))}
        </>
      );
    }
    return null;
  }, [
    JSON.stringify(activeForms),
    JSON.stringify(values),
    formsEditedByUsers,
    editingFormId,
    duplicateReceiverData,
    duplicateCardData,
  ]);
  const formFields = isArrayHasItems(forms)
    ? getFormsFromFields({
        formList: forms,
        indexOffset: 0,
        descirption: __("Dane osób, dla których wybierasz abonament."),
        isMyBenefitsView,
      })
    : null;

  if (isLoading) {
    return <Loading className={classes.loader} />;
  }

  return formFields || activeFields ? (
    <div className={classes.root}>
      <FieldArray name={`${benefitId}.fieldArray`}>{formFields}</FieldArray>
      {activeFields}
    </div>
  ) : null;
};

CheckoutSubscriptionBenefitForm.propTypes = {
  classes: shape({}).isRequired,
  forms: arrayOf(shape({})),
  activeForms: arrayOf(shape({})),
  setModal: func,
  benefitId: string.isRequired,
  wayChooseOthersInActiveForm: string.isRequired,
  benefitUUID: string.isRequired,
  isMyBenefitsView: bool.isRequired,
  handleItemUpdate: func,
  handleToggleEditModal: func,
  formsEditedByUsers: arrayOf(string).isRequired,
  setFormsEditedByUsers: func.isRequired,
  onFormUpdateSucceeded: func,
  formsToUpdate: shape({}),
  outerFormValues: object,
};

CheckoutSubscriptionBenefitForm.defaultProps = {
  forms: [],
  activeForms: [],
  setModal: () => {},
  handleItemUpdate: null,
  handleToggleEditModal: null,
  onFormUpdateSucceeded: null,
  formsToUpdate: DEFAULT_FORMS_TO_UPDATE,
  outerFormValues: {},
};

export default classify(defaultClasses)(CheckoutSubscriptionBenefitForm);
